import { useContext, useEffect, useMemo } from 'react';
import { Stack, Typography, Button, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import CountContext from '../../../context/countContext';
import { useAuth } from '../../../context/authContext';
import useHttp from '../../../hooks/useHttp';
import DashboardWidgetCard from '../DashboardWidgetCard';
import { formatCountBadge } from '../../../util/formatCountBadge';

// Renders an actionable counter widget. It plugs into the existing CountContext
// (the same source as the sidebar badges), so the value is push-driven via
// Socket.io and shared/deduplicated across the whole app — no extra polling.
export default function CountWidget({ widget, editMode, onHide, dragHandleProps }) {
  const navigate = useNavigate();
  const countCtx = useContext(CountContext);
  const { getAuthHeaders } = useAuth();
  const { sendRequest } = useHttp();
  const { countKey, countUrl } = widget;

  useEffect(() => {
    if (!countKey || !countUrl) return undefined;
    const fetchCount = async () => {
      try {
        const res = await sendRequest({ url: countUrl, method: 'GET', headers: getAuthHeaders() });
        countCtx.setCount(countKey, typeof res?.count === 'number' ? res.count : 0);
      } catch {
        countCtx.setCount(countKey, 0);
      }
    };
    // Registering runs the fetch once immediately and keeps the value live via
    // server-pushed `counts:invalidate` events; ref-counted so it coexists with
    // the sidebar badge using the same key.
    countCtx.registerInterval(countKey, fetchCount);
    return () => countCtx.unregisterInterval(countKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countKey, countUrl]);

  const rawValue = countKey ? countCtx.counts[countKey] : undefined;
  const value = typeof rawValue === 'number' ? rawValue : null;
  const hasItems = value != null && value > 0;

  // Quiet widgets (nothing to do) lose their attention accent so the board
  // visually surfaces only what actually needs action.
  const accent = useMemo(() => {
    if (widget.attention && !hasItems) return 'success';
    return widget.accent || 'primary';
  }, [widget.attention, widget.accent, hasItems]);

  return (
    <DashboardWidgetCard
      title={widget.title}
      icon={widget.icon}
      accent={accent}
      link={widget.link}
      loading={value == null}
      editMode={editMode}
      onHide={onHide}
      dragHandleProps={dragHandleProps}
      headerAction={
        widget.attention && hasItems ? (
          <Chip label='Handlungsbedarf' color='warning' size='small' sx={{ height: 20, fontSize: '0.65rem' }} />
        ) : null
      }
    >
      <Stack spacing={1} sx={{ flexGrow: 1 }} justifyContent='space-between'>
        <Stack direction='row' alignItems='baseline' spacing={1}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.3rem' }, lineHeight: 1, color: `${accent}.main` }}>
            {formatCountBadge(value ?? 0)}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {widget.unit || ''}
          </Typography>
        </Stack>
        {widget.description && (
          <Typography variant='caption' color='text.secondary'>
            {widget.description}
          </Typography>
        )}
        {widget.link && !editMode && (
          <Button
            size='small'
            variant={hasItems ? 'contained' : 'text'}
            color={hasItems ? (widget.attention ? 'warning' : 'primary') : 'inherit'}
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(widget.link)}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          >
            {hasItems ? 'Bearbeiten' : 'Öffnen'}
          </Button>
        )}
      </Stack>
    </DashboardWidgetCard>
  );
}
